import { useMemo } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, SectionTitle } from "@/components/mnf/UI";
import ChallengeCard from "@/components/mnf/ChallengeCard";
import Icon from "@/components/mnf/Icon";
import { queries, useStoreQuery } from "@/lib/store";

/**
 * Together — the Nourish Together challenge board.
 *
 * Threshold-not-leaderboard philosophy: every sister who reaches the
 * gentle target inside the window is celebrated equally. There is no
 * ranking, no streak penalty, and the launch challenge carries
 * founding member half-off pricing kept for life.
 */
export default function Together() {
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: GardenWalkTogetherCard and ChallengeCard
  // below read per-user progress. See the early return just below.
  const userId = me?.id ?? null;

  const featured = useStoreQuery(queries.featuredChallenges) ?? [];
  const allChallenges = useStoreQuery(queries.challenges) ?? [];
  const launch = useStoreQuery(queries.launchChallenge);
  const foundingCount = useStoreQuery(queries.foundingMemberCount) ?? 0;

  const featuredIds = useMemo(
    () => new Set(featured.map((c) => c.id)),
    [featured],
  );
  const otherCircles = useMemo(
    () => allChallenges.filter((c) => !featuredIds.has(c.id)),
    [allChallenges, featuredIds],
  );

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see Nourish Together, sister.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <ScreenHeader
        eyebrow="Sisterhood circles"
        title={<>Nourish Together</>}
        subtitle={
          <>
            We walk the same path, at our own pace. No leaderboards, just a
            quiet line that says, you made it, sister.
          </>
        }
      />

      <div className="px-5 mt-2">
        {launch && (
          <GlassCard
            organic
            padding={18}
            className="mb-5"
            data-testid="card-founding-counter"
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(140deg, #C49B5B 0%, #A07820 100%)",
                  color: "#F5F0E8",
                }}
                aria-hidden
              >
                <Icon name="leaf" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="mnf-eyebrow mb-1"
                  style={{ color: "var(--mnf-gold)" }}
                >
                  Founding circle
                </p>
                <h3
                  className="mnf-serif"
                  style={{
                    fontSize: 18,
                    fontWeight: 500,
                    color: "var(--mnf-fd)",
                    lineHeight: 1.25,
                  }}
                >
                  {foundingCount}{" "}
                  {foundingCount === 1 ? "sister" : "sisters"} walking with
                  Kiley
                </h3>
                <p
                  className="mnf-body mt-1"
                  style={{
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: "var(--mnf-tg)",
                  }}
                  data-testid="text-founding-summary"
                >
                  Founding members keep half-off pricing for life on
                  Seedling, Rooted, or Flourishing. Open while the launch
                  circle is gathering.
                </p>
              </div>
            </div>
          </GlassCard>
        )}

        <div className="mb-3">
          <SectionTitle
            eyebrow="A different kind of challenge"
            title="Threshold, not leaderboard"
          />
          <p
            className="mnf-body mt-2"
            style={{
              fontSize: 13.5,
              lineHeight: 1.6,
              color: "var(--mnf-tg)",
              maxWidth: 460,
            }}
          >
            Reach the gentle goal inside the window and you finish the
            circle. Miss a day, take a Sabbath, breathe. Grace days are
            built in. Every sister who crosses the line is celebrated the
            same way.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <PillTag tone="forest">Grace days built in</PillTag>
            <PillTag tone="purple">No public streaks</PillTag>
            <PillTag tone="fiber">Walk at your pace</PillTag>
          </div>
        </div>

        <div className="mt-6">
          <SectionTitle eyebrow="Walking together" title="Monthly Garden Walk" />
          <div className="mt-3">
            <GardenWalkTogetherCard userId={userId} />
          </div>
        </div>

        {featured.length > 0 && (
          <div className="mt-6">
            <SectionTitle
              eyebrow="Open now"
              title="Featured circles"
              trailing={
                <span data-testid="text-featured-count">
                  {featured.length} open
                </span>
              }
            />
            <div className="mt-3 flex flex-col gap-3">
              {featured.map((c) => (
                <ChallengeCard
                  key={c.id}
                  challenge={c}
                  userId={userId}
                  variant="list"
                />
              ))}
            </div>
          </div>
        )}

        {otherCircles.length > 0 && (
          <div className="mt-6">
            <SectionTitle
              eyebrow="More to explore"
              title="All circles"
            />
            <div className="mt-3 flex flex-col gap-3">
              {otherCircles.map((c) => (
                <ChallengeCard
                  key={c.id}
                  challenge={c}
                  userId={userId}
                  variant="list"
                />
              ))}
            </div>
          </div>
        )}

        {featured.length === 0 && otherCircles.length === 0 && (
          <GlassCard organic padding={22} className="mt-4">
            <p
              className="mnf-serif italic"
              style={{
                fontSize: 16,
                color: "var(--mnf-fd)",
                lineHeight: 1.4,
              }}
            >
              The circles are resting between seasons.
            </p>
            <p
              className="mnf-body mt-2"
              style={{ fontSize: 13.5, color: "var(--mnf-tg)" }}
            >
              Check back soon, sister. Kiley plants new circles each month.
            </p>
          </GlassCard>
        )}

        <div className="mt-6">
          <Link
            href="/sisters"
            className="mnf-body inline-flex items-center gap-2 no-underline"
            style={{
              color: "var(--mnf-f)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
            data-testid="link-back-sisters"
          >
            <Icon name="arrow-left" size={14} />
            Back to Sisters
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- Monthly Garden Walk on Together ------------------- */

const FRUIT_LABEL_TG: Record<string, string> = {
  love: "Love",
  joy: "Joy",
  peace: "Peace",
  patience: "Patience",
  kindness: "Kindness",
  goodness: "Goodness",
  faithfulness: "Faithfulness",
  gentleness: "Gentleness",
  "self-control": "Self-control",
};

/**
 * Garden Walk entry on the Together board. We render it as its own card
 * rather than a Challenge so it sits parallel to the Nourish Together
 * launch challenge and uses the fiber/forest tone of the walk.
 */
function GardenWalkTogetherCard({ userId }: { userId: string }) {
  const month = useStoreQuery(queries.currentGardenWalkMonth);
  const stats = useStoreQuery(() =>
    month ? queries.gardenWalkStats(userId, month.id) : null,
  );
  const summary = useStoreQuery(() =>
    month ? queries.gardenWalkMonthSummary(month.id) : null,
  );
  if (!month || !stats || !summary) return null;
  const fruitLabel = FRUIT_LABEL_TG[month.fruit] ?? month.fruit;
  const pct = Math.min(1, stats.walks / Math.max(1, stats.target));

  return (
    <Link
      href="/garden-walk"
      className="no-underline block"
      data-testid="link-together-garden-walk"
    >
      <GlassCard
        organic
        padding={18}
        style={{
          background:
            "linear-gradient(135deg, rgba(168,212,184,0.22) 0%, rgba(245,240,232,0.7) 100%)",
          border: "1px solid rgba(90,140,112,0.32)",
          cursor: "pointer",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="shrink-0 inline-flex items-center justify-center"
            style={{
              width: 48,
              height: 48,
              borderRadius: "20px 8px 20px 8px",
              background: "var(--mnf-f)",
              color: "#F5F0E8",
              fontSize: 22,
            }}
          >
            {month.hero.primary}
          </div>
          <div className="flex-1 min-w-0">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 2 }}>
              Fruit of the Spirit · {fruitLabel}
            </p>
            <p
              className="mnf-serif"
              style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}
            >
              {month.title}
            </p>
            <p
              className="mnf-body italic"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 4 }}
            >
              "{month.scripture}"
              <span style={{ color: "var(--mnf-t)" }}> · {month.scriptureRef}</span>
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <PillTag tone="forest">{summary.sisters} sisters walking</PillTag>
              <PillTag tone="fiber">{summary.walks} walks this month</PillTag>
            </div>
            <div className="mt-3">
              <div
                style={{
                  height: 6,
                  borderRadius: 999,
                  background: "rgba(30,74,53,0.10)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pct * 100}%`,
                    height: "100%",
                    background: "var(--mnf-f)",
                    transition: "width 360ms ease",
                  }}
                />
              </div>
              <p
                className="mnf-body"
                style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 6 }}
              >
                Your path, {stats.walks} of {stats.target} walks
              </p>
            </div>
          </div>
          <Icon name="chevron_right" size={20} weight={400} color="var(--mnf-tg)" />
        </div>
      </GlassCard>
    </Link>
  );
}
